import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from 'session-manager/views/login-form/LoginForm';
import { SessionManagerService } from 'session-manager/services/SessionManagerService';

import {
  Form,
  FormControl,
  FormGroup,
  PasswordFormField,
  ShortTextFormField
} from '@client:common/form';
import { Validators } from '@client:common/form/validation';
import { Button, IconButton } from '@client:common/button';
import { Icon } from '@client:common/icon';
import { Dialog } from '@client:common/overlay/dialog';
import { Ripple } from '@client:common/effects/ripple';
import { ProgressIndicator } from '@client:common/overlay/progress-indicator';
import { GlobalStore } from '@client:common/global-store';
import { Notification } from '@client:common/overlay/notification';


import { CreateAccountDto } from '../../models/CreateAccountDto';
import './Registration.dark.scss';
import './Registration.light.scss';

interface Props {
  onSubmit: (createUserDto: CreateAccountDto) => void;
}

export const Registration = (props: Props) => {
  const formGroupRef = useRef(
    new FormGroup<CreateAccountDto>({
      email: new FormControl('', [Validators.checkValidEmail()]),
      password: new FormControl('', [Validators.checkPassword()]),
      firstName: new FormControl('', [Validators.checkNotEmpty()]),
      lastName: new FormControl('', [Validators.checkNotEmpty()]),
      organization: new FormControl('', [Validators.checkNotEmpty()])
    })
  );
  const navigate = useNavigate();

  const [isFormValid, setIsFormValid] = useState(
    formGroupRef.current.isValid()
  );

  useEffect(() => {
    const subscription = formGroupRef.current.validityChanges().subscribe({
      next: setIsFormValid
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const onSubmit = () => {
    props.onSubmit(formGroupRef.current.getValue());
  };

  const onShowLoginForm = () => {
    const dialogRef = Dialog.create(
      <>
        <IconButton
          className='login-dialog__close'
          style='basic'
          color='primary'
          onClick={() => {
            dialogRef.close();
          }}
        >
          <Ripple
fixed
duration={1000} />
          <Icon icon='close' />
        </IconButton>
        <LoginForm
          onLogin={(email, password) => {
            const sessionManagerService = SessionManagerService.getInstance();

            ProgressIndicator.show();
            sessionManagerService
              .signIn(email, password)
              .then((authenticatedAccount) => {
                const globalStore = GlobalStore.getInstance();

                globalStore.update({
                  authenticatedAccount
                });
                dialogRef.close();
                navigate('/');
              })
              .catch(() => {
                Notification.open(
                  'Email or password is incorrect, please try again'
                );
              })
              .finally(() => {
                ProgressIndicator.hide();
              });
          }}
        />
      </>
    )
      .addClassName('login-dialog')
      .open();
  };

  return (
    <div className='registration'>
      <h1>Sign up</h1>

      <Form className='registration__form'>
        <ShortTextFormField
          label='Email'
          type='email'
          formControl={formGroupRef.current.findControl('email')}
        />
        <PasswordFormField
          showPasswordHint
          label='Password'
          formControl={formGroupRef.current.findControl('password')}
        />
        <div className='name-container'>
          <ShortTextFormField
            label='First Name'
            formControl={formGroupRef.current.findControl('firstName')}
          />
          <ShortTextFormField
            label='Last Name'
            formControl={formGroupRef.current.findControl('lastName')}
          />
        </div>
        <ShortTextFormField
          label='Organization'
          formControl={formGroupRef.current.findControl('organization')}
        />
        <div className='action-controls_container'>
          <IconButton
            style='basic'
            color='primary'
            onClick={onShowLoginForm}
            className='log-in-trigger'
          >
            <Icon icon='open_in_new' />
            LOG IN INSTEAD
          </IconButton>
          <Button
            style='raised'
            color='primary'
            disabled={!isFormValid}
            onClick={onSubmit}
          >
            CONTINUE TO VERIFICATION
          </Button>
        </div>
      </Form>
    </div>
  );
};
